import {
  showOlbAddIncomeStep,
  showOlbEmploymentDetailsStep,
  showOlbLivingArrangementStep,
} from "./olbStepConditions";
import type { FlowStep, NavigationContext } from "../flowConfig";

function isMembershipSelectionStep(context: NavigationContext) {
  return context.applicationState === "membership_selection";
}

function canShowMembershipVerifyEmailStep(context: NavigationContext) {
  return isMembershipSelectionStep(context) || isMembershipSharePendingStep(context);
}

function isMembershipSharePendingStep(context: NavigationContext) {
  return context.applicationState === "membership_share_pending";
}

function canShowMembershipShareStatusStep(context: NavigationContext) {
  return isMembershipSharePendingStep(context) || Boolean(context.accountApplicationUuid);
}

function canShowCoApplicantEmploymentDetailsStep(context: NavigationContext) {
  return (
    (context.isCoApplicantEnabled ?? false) &&
    context.hasCoApplicant === true &&
    (context.showEmploymentDetailsStep ?? true)
  );
}

export const personalLoanFlow: Record<"web" | "olb", FlowStep[]> = {
  web: [
    // Eligibility selection (only shown if institution has eligibility criteria)
    {
      id: "eligibility",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/personal-loan/${applicationUuid}/eligibility`
          : "/personal-loan/eligibility",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/personal-loan/eligibility", `/personal-loan/${applicationUuid}/eligibility`]
          : ["/personal-loan/eligibility"],
      condition: (context: NavigationContext) => context.hasEligibilityCriteria === true,
    },

    // MLA covered-borrower question (LNDG-1483 backport; shown when the
    // mla_question additional field is enabled)
    {
      id: "questions",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/personal-loan/${applicationUuid}/questions`
          : "/personal-loan/questions",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/personal-loan/questions", `/personal-loan/${applicationUuid}/questions`]
          : ["/personal-loan/questions"],
      condition: (context: NavigationContext) => context.hasLendingQuestions === true,
    },

    // Primary applicant information
    {
      id: "phone",
      getPath: ({ applicationUuid }) =>
        applicationUuid ? `/personal-loan/${applicationUuid}/phone` : "/personal-loan/phone",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/personal-loan/phone", `/personal-loan/${applicationUuid}/phone`]
          : ["/personal-loan/phone"],
      condition: (context: NavigationContext) => context.isPlaidLayerEnabled === true,
    },
    {
      id: "personal-info-review",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/personal-loan/${applicationUuid}/personal-info-review`
          : "/personal-loan/personal-info-review",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? [
              "/personal-loan/personal-info-review",
              `/personal-loan/${applicationUuid}/personal-info-review`,
            ]
          : ["/personal-loan/personal-info-review"],
      condition: (context: NavigationContext) =>
        context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill !== true,
    },
    {
      id: "personal-info",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/personal-loan/${applicationUuid}/personal-info`
          : "/personal-loan/personal-info",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/personal-loan/personal-info", `/personal-loan/${applicationUuid}/personal-info`]
          : ["/personal-loan/personal-info"],
      condition: (context: NavigationContext) =>
        (context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill === true) ||
        context.isPlaidLayerEnabled !== true,
    },
    {
      id: "address",
      path: "/personal-loan/:applicationUuid/address",
      condition: (context: NavigationContext) =>
        (context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill === true) ||
        context.isPlaidLayerEnabled !== true,
    },
    { id: "requested-amount", path: "/personal-loan/:applicationUuid/requested-amount" },
    {
      id: "living-arrangement",
      path: "/personal-loan/:applicationUuid/living-arrangement",
      condition: (context: NavigationContext) => context.showLivingArrangementStep ?? true,
    },
    { id: "add-income", path: "/personal-loan/:applicationUuid/add-income" },
    {
      id: "employment-details",
      path: "/personal-loan/:applicationUuid/employment-details",
      condition: (context: NavigationContext) => context.showEmploymentDetailsStep ?? true,
    },

    // Co-applicant flow (conditional)
    {
      id: "co-applicant-ask",
      path: "/personal-loan/:applicationUuid/co-applicant/ask",
      condition: (context: NavigationContext) => context.isCoApplicantEnabled ?? false,
    },
    {
      id: "co-applicant-details",
      path: "/personal-loan/:applicationUuid/co-applicant/details",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address-ask",
      path: "/personal-loan/:applicationUuid/co-applicant/address-ask",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address",
      path: "/personal-loan/:applicationUuid/co-applicant/address",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) &&
        context.hasCoApplicant === true &&
        context.hasSeparateCoApplicantAddress === true,
    },
    {
      id: "co-applicant-income",
      path: "/personal-loan/:applicationUuid/co-applicant/income",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-employment-details",
      path: "/personal-loan/:applicationUuid/co-applicant/employment-details",
      condition: canShowCoApplicantEmploymentDetailsStep,
    },

    // Final steps
    { id: "disclosures", path: "/personal-loan/:applicationUuid/disclosures" },
    { id: "credit-pull", path: "/personal-loan/:applicationUuid/credit-pull" },
    { id: "offers", path: "/personal-loan/:applicationUuid/offers" },
    {
      id: "membership-share-select",
      path: "/personal-loan/:applicationUuid/membership-share/select",
      condition: isMembershipSelectionStep,
    },
    {
      id: "membership-share-use-different-email",
      path: "/personal-loan/:applicationUuid/membership-share/use-different-email",
      condition: canShowMembershipVerifyEmailStep,
    },
    {
      id: "membership-share-verify-email",
      path: "/personal-loan/:applicationUuid/membership-share/verify-email",
      condition: canShowMembershipVerifyEmailStep,
    },
    {
      id: "membership-share-activate",
      path: "/personal-loan/:applicationUuid/membership-share/activate",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-disclosures",
      path: "/personal-loan/:applicationUuid/membership-share/disclosures",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-processing",
      path: "/personal-loan/:applicationUuid/membership-share/processing",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-documents-requested",
      path: "/personal-loan/:applicationUuid/membership-share/documents-requested",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-submitting-documents",
      path: "/personal-loan/:applicationUuid/membership-share/submitting-documents",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-status",
      path: "/personal-loan/:applicationUuid/membership-share/status",
      condition: canShowMembershipShareStatusStep,
    },
    { id: "submitted", path: "/personal-loan/:applicationUuid/submitted" },
  ],
  olb: [
    // Application start
    { id: "confirm-info", path: "/olb-personal-loan/:applicationUuid/confirm-info" },

    // Primary applicant information
    { id: "requested-amount", path: "/olb-personal-loan/:applicationUuid/requested-amount" },
    {
      id: "living-arrangement",
      path: "/olb-personal-loan/:applicationUuid/living-arrangement",
      condition: showOlbLivingArrangementStep,
    },
    {
      id: "add-income",
      path: "/olb-personal-loan/:applicationUuid/add-income",
      condition: showOlbAddIncomeStep,
    },
    {
      id: "employment-details",
      path: "/olb-personal-loan/:applicationUuid/employment-details",
      condition: showOlbEmploymentDetailsStep,
    },

    // Co-applicant flow (conditional)
    {
      id: "co-applicant-ask",
      path: "/olb-personal-loan/:applicationUuid/co-applicant/ask",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant !== false,
    },
    {
      id: "co-applicant-details",
      path: "/olb-personal-loan/:applicationUuid/co-applicant/details",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address",
      path: "/olb-personal-loan/:applicationUuid/co-applicant/address",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-income",
      path: "/olb-personal-loan/:applicationUuid/co-applicant/income",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-employment-details",
      path: "/olb-personal-loan/:applicationUuid/co-applicant/employment-details",
      condition: canShowCoApplicantEmploymentDetailsStep,
    },

    // Final steps
    { id: "disclosures", path: "/olb-personal-loan/:applicationUuid/disclosures" },
    { id: "credit-pull", path: "/olb-personal-loan/:applicationUuid/credit-pull" },
    { id: "offers", path: "/olb-personal-loan/:applicationUuid/offers" },
    { id: "submitted", path: "/olb-personal-loan/:applicationUuid/submitted" },
  ],
};
